import {
  Camera,
  Film,
  Flame,
  Palette,
  SlidersHorizontal,
  Sparkles,
  Wand,
  Waves,
  type LucideIcon,
} from "lucide-react";
import type { CSSProperties, DragEvent, Ref } from "react";

import type { EffectKind } from "../../api/generated/model";
import {
  EFFECTS,
  EFFECT_CATEGORIES,
  EFFECT_DRAG_TYPE,
  searchEffects,
  type EffectInfo,
} from "./effect-catalog";
import { TILE_PREVIEW_DELAY_MS, useEffectPreview } from "./hover-previews";
import { TileCatalog } from "./TileCatalog";
import classes from "./transitions.module.css";

/** Live's card for an effect whose preview did not load: a wash and an icon in a dark round. */
interface CardLook {
  pattern: string;
  Icon: LucideIcon;
}

// Live's eight cards: its seven categories and one for any other. Live sets the wash as the
// card's background image, over its gradient class, so the wash is what shows.
const LOOKS: Record<"light" | "camera" | "glitch" | "retro" | "distort" | "sparkles" | "color" | "other", CardLook> = {
  light: {
    pattern: "radial-gradient(circle at center, rgba(255,255,255,0.8) 0%, rgba(245,158,11,0.4) 40%, rgba(220,38,38,0.2) 100%)", // raw-color: live's card
    Icon: Flame,
  },
  camera: {
    pattern: "radial-gradient(circle at center, rgba(59,130,246,0.5) 0%, rgba(30,58,138,0.3) 60%, transparent 100%)", // raw-color: live's card
    Icon: Camera,
  },
  glitch: {
    pattern: "repeating-linear-gradient(0deg, rgba(236,72,153,0.3) 0px, rgba(6,182,212,0.3) 4px, transparent 4px, transparent 8px)", // raw-color: live's card
    Icon: SlidersHorizontal,
  },
  retro: {
    pattern: "radial-gradient(circle at center, rgba(245,158,11,0.3) 0%, transparent 70%)", // raw-color: live's card
    Icon: Film,
  },
  distort: {
    pattern: "radial-gradient(circle at center, rgba(20,184,166,0.5) 0%, rgba(13,148,136,0.3) 50%, transparent 100%)", // raw-color: live's card
    Icon: Waves,
  },
  sparkles: {
    pattern: "radial-gradient(circle at center, rgba(244,114,182,0.5) 0%, transparent 60%)", // raw-color: live's card
    Icon: Sparkles,
  },
  color: {
    pattern: "linear-gradient(135deg, rgba(139,92,246,0.4) 0%, rgba(79,70,229,0.3) 100%)", // raw-color: live's card
    Icon: Palette,
  },
  other: {
    pattern: "radial-gradient(circle at center, rgba(255,255,255,0.15) 0%, transparent 70%)", // raw-color: live's card
    Icon: Wand,
  },
};

/**
 * Live's card for a tile: its category first, then the words of its key, in live's order, so a
 * swirl that moves looks like Camera and a neon grade like Light, as live picks them.
 */
export function lookOf({ category, key }: EffectInfo): CardLook {
  const words = key.toLowerCase();
  if (category === "light" || /flash|strobe|neon|shine/.test(words)) return LOOKS.light;
  if (category === "camera" || /zoom|shake|move|spin/.test(words)) return LOOKS.camera;
  if (category === "glitch" || /rgb|vhs|scan/.test(words)) return LOOKS.glitch;
  if (category === "retro" || /film|sepia|grain/.test(words)) return LOOKS.retro;
  if (category === "distort" || /wave|swirl|ripple|heat/.test(words)) return LOOKS.distort;
  if (category === "sparkles" || /heart|star|bubble/.test(words)) return LOOKS.sparkles;
  if (category === "color" || /hdr|duo|tri|vignette/.test(words)) return LOOKS.color;
  return LOOKS.other;
}

export interface EffectsTabProps {
  /** False when the member may not change the project: no tile applies. */
  canApply: boolean;
  /** Live's tile click, or its drop on the timeline: the effect from 0 to 5 s on a new top track. */
  onApply: (kind: EffectKind) => void;
}

/**
 * Live's Effects tab (#265): "Search effects...", a row per category with "View all", or one
 * category's or the search's tiles in a grid. A tile is live's card over its name, with the
 * CUSTOM badge on live's custom shaders; a click or a drop adds it from 0 to 5 s on a new top
 * track, as live's do.
 */
export function EffectsTab(props: EffectsTabProps) {
  return (
    <TileCatalog
      noun="effects"
      categories={EFFECT_CATEGORIES}
      items={EFFECTS}
      categoryOf={(info) => info.category}
      keyOf={(info) => info.key}
      search={searchEffects}
      renderTile={(info) => <Tile info={info} {...props} />}
      NoneIcon={Sparkles}
    />
  );
}

/**
 * Live's card: its wash, an icon in a dark round, the name in small white letters; over it
 * `slot`, where its hover preview plays (#265 part 3b), and the badge over that.
 */
function EffectCard({ info, slot }: { info: EffectInfo; slot: Ref<HTMLSpanElement> }) {
  const { pattern, Icon } = lookOf(info);
  return (
    <span className={classes.card} style={{ "--card-pattern": pattern } as CSSProperties} aria-hidden>
      <span className={classes.cardIcon}>
        <Icon size={14} />
      </span>
      <span className={classes.cardName}>{info.name}</span>
      <span ref={slot} className={classes.preview} />
      {info.source === "custom" ? <span className={classes.customBadge}>CUSTOM</span> : null}
    </span>
  );
}

function Tile({ info, canApply, onApply }: EffectsTabProps & { info: EffectInfo }) {
  const { kind } = info;
  const { slot, hover } = useEffectPreview(kind, TILE_PREVIEW_DELAY_MS);
  const face = (
    <>
      <EffectCard info={info} slot={slot} />
      <span className={classes.tileName}>{info.name}</span>
    </>
  );
  const name = info.source === "custom" ? `${info.name}, custom` : info.name;
  const start = (event: DragEvent<HTMLButtonElement>) => {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData(EFFECT_DRAG_TYPE, kind);
  };
  return (
    <button
      type="button"
      className={classes.tile}
      aria-label={name}
      disabled={!canApply}
      draggable={canApply}
      onClick={() => onApply(kind)}
      onDragStart={start}
      {...hover}
    >
      {face}
    </button>
  );
}
